// The agent inspector container (APPLE-SPEC §6.3, §5.12; shell-ia §12): a macOS-inspector column at the end side (the left in Arabic)
// between the chrome and the status bar — 360 px (resizable 360–560), the sidebar material, a 0.5 px leading edge — its 52 px rail
// (1024–1279 px, or folded), the overlay that floats over the content from the rail, and the sheet below 1024 px. The content is
// WP-SIDEBAR's: `ChatSidebar` (the conversation) and `SidebarRail`; the phone dock's Ask field is its `AskBar`. WP-SHELL.
//
// The conversation never unmounts when the column folds (builder §17.6: "deep research … keeps running when the sidebar collapses"): it is
// rendered once into a detached host element that moves between the column, a hidden stash (rail, closed sheet) and the sheet, so a
// stream in progress, the draft and the scroll position survive folding, the overlay and the sheet. The sidebar's code loads on demand
// (it brings the conversation, markdown and charts), so the first paint of a screen does not wait for it.
//
// An agent's own pages (`/m/*`, 16 Oct 2026) have no column: the agent's chat box is on the page, and the dock's questions there (✦ Dive
// in, a routine's "Set up", "Ask about this card") go to that agent (agent/moduleAgent.tsx `ModuleAgentDock`, mounted here); the column's
// conversation is not started on them.
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { ArrowUp } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Sheet, SheetContent, SheetTitle } from "@ncp/ui";
import { AgentAvatar, EconicMark } from "../brand";
import { useDock, useDockAvailable, useDockOpen } from "../../shell/dock";
import { withLocales } from "../../app/locales-ready";
import { routeFor } from "../../app/routes";
import { ModuleAgentDock, moduleOfPath } from "../agent/moduleAgent";
import { SIDEBAR_MAX, SIDEBAR_MIN, useSidebarState } from "./sidebar-state";
import { BELOW_STRIP } from "./sheet";

const ChatSidebar = lazy(withLocales(() => import("../sidebar/ChatSidebar").then((m) => ({ default: m.ChatSidebar }))));
const SidebarRail = lazy(withLocales(() => import("../sidebar/SidebarRail").then((m) => ({ default: m.SidebarRail }))));
const AskBar = lazy(withLocales(() => import("../sidebar/AskBar").then((m) => ({ default: m.AskBar }))));
/** The sidebar's stylesheet and module side effects (UI_EVENT listeners) come with its index. */
const loadSidebar = () => import("../sidebar");

/** A DOM element made once and moved between slots; whatever is portaled into it keeps its React state. */
function useHost(): HTMLDivElement | null {
  return useMemo(() => {
    if (typeof document === "undefined") return null;
    const el = document.createElement("div");
    el.className = "es-agent-host";
    el.style.display = "contents";
    return el;
  }, []);
}

/** Where the host is shown: appends it on mount (moving it out of the previous slot). */
function Slot({ host, className, hidden }: { host: HTMLDivElement | null; className?: string; hidden?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && host && host.parentElement !== el) el.appendChild(host);
  });
  return <div ref={ref} className={className} hidden={hidden} data-agent-slot="" />;
}

export function AgentColumn() {
  const sidebar = useSidebarState();
  const { t } = useTranslation();
  const dock = useDock();
  const dockOpen = useDockOpen();
  const available = useDockAvailable();
  const [drag, setDrag] = useState(false);
  const effective = sidebar.effective;
  const showing = effective === "open" || effective === "overlay" || effective === "sheet";
  const host = useHost();
  const { pathname } = useLocation();
  const columnless = routeFor(pathname)?.sidebar === "none";
  const agentPage = moduleOfPath(pathname);
  // The conversation mounts the first time the agent can show and then stays (folding never aborts it); a screen without the column
  // (an agent's page, a conversation full screen) does not start it.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (available && !columnless && !started) {
      setStarted(true);
      void loadSidebar();
    }
  }, [available, columnless, started]);
  const moduleDock = agentPage && available ? <ModuleAgentDock key={agentPage} module={agentPage} /> : null;

  // A screen that asks the agent something (`useDock().ask`) opens the column; the column's own state drives the dock panels' `open`.
  const last = useRef({ open: dockOpen, panel: dock.panelId });
  useEffect(() => {
    const before = last.current;
    last.current = { open: dockOpen, panel: dock.panelId };
    if (dockOpen && !before.open && before.panel === dock.panelId && !showing) sidebar.open();
  }, [dockOpen, dock.panelId, showing, sidebar]);
  useEffect(() => {
    if (dock.available && dock.open !== showing) dock.setOpen(showing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showing, dock.available, dock.panelId]);

  // Esc returns a floating overlay to the rail (unless a dialog, menu or popover holds it).
  useEffect(() => {
    if (effective !== "overlay") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [data-radix-popper-content-wrapper]')) return;
      sidebar.close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [effective, sidebar]);

  const variant = effective === "sheet" || effective === "hidden" ? "sheet" : "column";
  const conversation = host && started ? createPortal(<Suspense fallback={null}><ChatSidebar variant={variant} /></Suspense>, host) : null;

  if (effective === "hidden" || effective === "sheet") {
    return (
      <>
        {moduleDock}
        {conversation}
        {/* closed: the conversation waits in a hidden stash */}
        {effective === "hidden" ? <Slot host={host} hidden /> : null}
        <Sheet open={effective === "sheet"} onOpenChange={(o) => sidebar.setSheetOpen(o)}>
          {/* Opening lands focus on the sheet itself, not on its first control (the department pop-up), so no ring flashes and no keyboard rises. */}
          <SheetContent side="end" width="max-w-md" className="gap-0 bg-[var(--material-sidebar)] p-0 outline-none [&>button.absolute]:hidden" style={BELOW_STRIP} aria-describedby={undefined}
            onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true }); }}>
            <SheetTitle className="sr-only">{t("assistant:econic.shell.sidebar.title")}</SheetTitle>
            <Slot host={host} className="flex h-full min-h-0 flex-col" />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  const overlay = effective === "overlay";
  const rail = effective === "rail";
  return (
    <aside
      className="es-agent econic-material-sidebar"
      data-effective={effective}
      data-overlay={overlay || undefined}
      data-drag={drag || undefined}
      aria-label={t("assistant:econic.shell.sidebar.title")}
      data-dock-panel=""
      data-print="hide"
      style={overlay ? { width: Math.min(sidebar.width, 440) } : undefined}
    >
      {moduleDock}
      {conversation}
      {rail ? <Suspense fallback={<div className="es-agent-rail" />}><SidebarRail /></Suspense> : null}
      {effective === "open" ? <ResizeHandle onDragging={setDrag} /> : null}
      {/* folded to the rail, the conversation stays mounted (hidden) so a stream in progress keeps running */}
      <Slot host={host} hidden={rail} className="flex h-full min-h-0 flex-col" />
    </aside>
  );
}

function ResizeHandle({ onDragging }: { onDragging: (on: boolean) => void }) {
  const { t } = useTranslation();
  const { dir } = useLocale();
  const sidebar = useSidebarState();
  const [active, setActive] = useState(false);
  const widthAt = (x: number) => (dir === "rtl" ? x : window.innerWidth - x);
  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setActive(true);
    onDragging(true);
    document.querySelector(".es-main")?.setAttribute("data-sidebar-drag", "");
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) sidebar.setWidth(widthAt(e.clientX));
  };
  const up = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    sidebar.setWidth(widthAt(e.clientX), true);
    setActive(false);
    onDragging(false);
    document.querySelector(".es-main")?.removeAttribute("data-sidebar-drag");
  };
  const key = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const grow = dir === "rtl" ? "ArrowRight" : "ArrowLeft";
    const shrink = dir === "rtl" ? "ArrowLeft" : "ArrowRight";
    if (e.key === grow) { e.preventDefault(); sidebar.setWidth(sidebar.width + 24, true); }
    else if (e.key === shrink) { e.preventDefault(); sidebar.setWidth(sidebar.width - 24, true); }
  };
  return (
    <div role="separator" aria-orientation="vertical" aria-label={t("assistant:econic.shell.sidebar.resize")} aria-valuemin={SIDEBAR_MIN} aria-valuemax={SIDEBAR_MAX} aria-valuenow={sidebar.width}
      tabIndex={0} className="resize" data-active={active || undefined} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onKeyDown={key} />
  );
}

/** The phone dock's Ask field (< 768 px): WP-SIDEBAR's AskBar; while its code loads, a field of the same size that opens the sheet. */
export function PhoneAskBar() {
  const { t } = useTranslation();
  const sidebar = useSidebarState();
  const { pathname } = useLocation();
  const def = routeFor(pathname);
  if (def?.sidebar === "none") return null;
  const fallback = (
    <button type="button" className="ask" onClick={sidebar.open} data-ask-open="">
      <span className="ph"><AgentAvatar agent="econic" size={16} icon={<EconicMark size={16} />} /><span>{t("assistant:econic.shell.sidebar.askPlaceholder")}</span><span className="go" aria-hidden><ArrowUp /></span></span>
    </button>
  );
  return <Suspense fallback={fallback}><AskBar /></Suspense>;
}
